/**
 * Letter-first onboarding — "How would you like help?" bottom sheet.
 *
 * Orion's three modes, offered from the canvas and never as the default
 * path: polish what they wrote, interview-then-draft, or
 * interview-then-outline. Dismissing is always one tap.
 */
import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import { ms } from '../../utils/responsive';

export type OrionMode = 'improve' | 'draft' | 'outline';

const MODES: { id: OrionMode; title: string; sub: string; meta: string }[] = [
  {
    id: 'improve',
    title: 'Polish what I wrote',
    sub: 'Same words, same voice — just clearer.',
    meta: '2 min',
  },
  {
    id: 'draft',
    title: 'Ask me questions, then write it',
    sub: "I'll interview you and turn your answers into a draft.",
    meta: '5 min',
  },
  {
    id: 'outline',
    title: 'Ask me questions, then give me an outline',
    sub: 'You keep the writing. I just hand you the shape.',
    meta: '4 min',
  },
];

interface OrionHelpSheetProps {
  visible: boolean;
  hasContent: boolean;
  onPick: (mode: OrionMode) => void;
  onClose: () => void;
}

export const OrionHelpSheet: React.FC<OrionHelpSheetProps> = ({
  visible,
  hasContent,
  onPick,
  onClose,
}) => {
  const modes = hasContent ? MODES : MODES.filter((m) => m.id !== 'improve');

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.sheet} onPress={() => {}}>
          <Text style={styles.title}>How would you like help?</Text>
          {modes.map((mode) => (
            <TouchableOpacity
              key={mode.id}
              style={styles.card}
              onPress={() => onPick(mode.id)}
              accessibilityRole="button"
              accessibilityLabel={mode.title}
              accessibilityHint={mode.sub}
            >
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>{mode.title}</Text>
                <Text style={styles.cardMeta}>{mode.meta}</Text>
              </View>
              <Text style={styles.cardSub}>{mode.sub}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={styles.dismiss}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="No thanks, I've got it"
          >
            <Text style={styles.dismissText}>No thanks — I've got it</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28, 44, 79, 0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    padding: ms(20),
    paddingBottom: ms(32),
  },
  title: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(14),
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(10),
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(4),
  },
  cardTitle: {
    flex: 1,
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginRight: ms(8),
  },
  cardMeta: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#6B7280',
  },
  cardSub: {
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#6B7280',
  },
  dismiss: {
    alignItems: 'center',
    paddingVertical: ms(12),
  },
  dismissText: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
});

export default OrionHelpSheet;
